<template>
  <div class="ai-question-picker">
    <div
      v-if="showTabs"
      class="ai-question-picker__tabs"
      role="tablist"
      aria-label="Question type"
    >
      <button
        type="button"
        :class="{ 'ai-question-picker__tab--active': activeTab === 'templates' }"
        role="tab"
        :aria-selected="activeTab === 'templates'"
        @click="activeTab = 'templates'"
      >
        Templates
      </button>
      <button
        type="button"
        :class="{ 'ai-question-picker__tab--active': activeTab === 'custom' }"
        role="tab"
        :aria-selected="activeTab === 'custom'"
        @click="activeTab = 'custom'"
      >
        Write your own
      </button>
    </div>

    <div v-if="showTemplateQuestions" class="ai-question-picker__groups">
      <question-group-accordion
        v-for="(group, index) in groups"
        :key="group.id"
        :group="group"
        :selected-question-ids="selectedQuestionIds"
        :open-by-default="index === 0"
        @toggle-question="$emit('toggle-question', $event)"
        @delete-question="$emit('delete-custom-question', $event)"
      />
    </div>

    <div v-if="showCustomQuestions" class="ai-question-picker__custom">
      <div
        v-if="customQuestions.length"
        class="ai-question-picker__custom-questions"
        :class="{ 'ai-question-picker__custom-questions--inline': !showTabs }"
      >
        <question-list-item
          v-for="question in customQuestions"
          :key="question.id"
          :question="question"
          :selected="isSelected(question.id)"
          :deletable="isQuestionDeletable(question)"
          @toggle="$emit('toggle-question', question)"
          @delete="$emit('delete-custom-question', question)"
        />
      </div>

      <bulk-custom-question-input
        :existing-questions="allQuestions"
        @save="saveBulkQuestions"
      />
    </div>

    <div v-if="validationErrors.questions" class="ai-question-picker__error">
      {{ validationErrors.questions }}
    </div>
  </div>
</template>

<script>
import BulkCustomQuestionInput from './BulkCustomQuestionInput.vue'
import QuestionGroupAccordion from './QuestionGroupAccordion.vue'
import QuestionListItem from './QuestionListItem.vue'

export default {
  name: 'QuestionPicker',
  components: {
    BulkCustomQuestionInput,
    QuestionGroupAccordion,
    QuestionListItem,
  },
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    customQuestions: {
      type: Array,
      default: () => [],
    },
    selectedQuestionIds: {
      type: Array,
      default: () => [],
    },
    validationErrors: {
      type: Object,
      default: () => ({}),
    },
    allowSavedCustomDelete: {
      type: Boolean,
      default: true,
    },
    customDisplayMode: {
      type: String,
      default: 'tab',
      validator: (value) => ['tab', 'inline'].includes(value),
    },
  },
  data () {
    return {
      activeTab: 'templates',
    }
  },
  computed: {
    allQuestions () {
      return this.groups.reduce((questions, group) => (
        questions.concat(group.questions || [])
      ), []).concat(this.customQuestions)
    },
    showTabs () {
      return this.customDisplayMode === 'tab'
    },
    showTemplateQuestions () {
      return !this.showTabs || this.activeTab === 'templates'
    },
    showCustomQuestions () {
      return !this.showTabs || this.activeTab === 'custom'
    },
  },
  methods: {
    saveBulkQuestions (questions) {
      this.$emit('add-custom-questions', questions)
    },
    isSelected (questionId) {
      return this.selectedQuestionIds.includes(questionId)
    },
    isQuestionDeletable (question) {
      if (question.deletable) {
        return true
      }

      return this.allowSavedCustomDelete
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-question-picker {
  display: flex;
  min-height: 100%;
  flex-direction: column;
}

.ai-question-picker__tabs {
  display: flex;
  align-items: center;
  gap: 14px;
  border-bottom: 1px solid var(--v-gray12Color-base);
  border-radius: 10px;
  margin-bottom: 20px;
  justify-content: space-around;
  background-color: var(--v-gray7Color-base);
  padding: 4px;
}

.ai-question-picker__tabs button {
  border: 0;
  border-bottom: 5px solid transparent;
  background: transparent;
  color: #6B7B7E;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  padding: 10px;
  flex-grow: 1;
}

.ai-question-picker__tabs .ai-question-picker__tab--active {
  border-radius: 10px;
  background: var(--v-whiteColor-base);
  color: var(--v-profileAnchorColor-base);
  font-weight: 700;
}

.ai-question-picker__groups,
.ai-question-picker__custom-questions {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-right: 4px;
}

.ai-question-picker__custom {
  display: flex;
  flex-direction: column;
}

.ai-question-picker__custom-questions--inline {
  margin-top: 16px;
}

.ai-question-picker__error {
  color: var(--v-red11Color-base);
  font-size: 12px;
  letter-spacing: 0.12px;
  margin-top: 10px;
  text-align: center;
}
</style>
